/**
 * ContactChannelChips - os canais em que a MESMA pessoa conversa com a empresa
 * (lead unificado nos 3 canais: WhatsApp, Instagram, Facebook).
 *
 * Um componente, quatro lugares: o cartao do contato (drawer), a faixa abaixo
 * do cabecalho dos 4 inboxes ("Tambem no WhatsApp"), o aviso de janela fechada
 * do Instagram e do Messenger ("Continuar no WhatsApp") e o Kanban usa a
 * mesma linguagem visual. A fonte e GET /api/contacts/{chave}/linked-contacts,
 * que com a flag `lead_multichannel` le as identidades do contato empresarial.
 *
 * Variantes:
 *  - "bar":    faixa fina "Tambem no ..." com um chip por canal; nada quando
 *              nao ha outro canal.
 *  - "drawer": secao "Canais" com o canal atual em destaque, os outros como
 *              botoes "abrir" e, quando `onLink` vier, o botao "Vincular a
 *              outro canal".
 *  - "cta":    so o botao "Continuar no WhatsApp" quando existe um WhatsApp
 *              vinculado (janela de 24 h fechada no IG/Messenger).
 *
 * Gated pela flag `lead_multichannel` do backend: desligada, renderiza nada.
 * A chave opaca ('instagram:{igsid}') viaja intacta; nunca vira telefone.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRightLeft, Facebook, Globe, Instagram, Link2, MessageCircle } from 'lucide-react';
import assignmentService from '../../services/global/assignmentService';
import useFeatureFlags from '../../hooks/useFeatureFlags';
import { linkedContactChannel, linkedContactChipLabel, linkedContactLabel, linkedContactTarget } from './linkedContacts';

const META = {
  whatsapp: { label: 'WhatsApp', Icon: MessageCircle, color: '#00a884' },
  wa_personal: { label: 'WhatsApp', Icon: MessageCircle, color: '#25d366' },
  wa_business: { label: 'WhatsApp Business', Icon: MessageCircle, color: '#00a884' },
  instagram: { label: 'Instagram', Icon: Instagram, color: '#c13584' },
  messenger: { label: 'Messenger', Icon: Facebook, color: '#1877f2' },
  widget: { label: 'Widget', Icon: Globe, color: '#00a884' },
};

const chipStyle = (color, clickable, colors) => ({
  display: 'inline-flex', alignItems: 'center', gap: 5,
  padding: '3px 9px', borderRadius: 999,
  backgroundColor: `${color}14`, color,
  border: `1px solid ${color}55`,
  fontSize: 11.5, fontWeight: 600, lineHeight: '16px',
  whiteSpace: 'nowrap', maxWidth: '100%',
  cursor: clickable ? 'pointer' : 'default',
  fontFamily: 'inherit',
  ...(clickable ? {} : { opacity: 0.95 }),
});

const Chip = ({ channel, label, onClick, title, current, colors }) => {
  const meta = META[channel] || META.whatsapp;
  const { Icon, color } = meta;
  const inner = (
    <>
      <Icon size={12} />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label || meta.label}</span>
      {current && (
        <span style={{ fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', opacity: 0.8 }}>
          aqui
        </span>
      )}
      {onClick && <ArrowRightLeft size={11} style={{ opacity: 0.8 }} />}
    </>
  );
  if (!onClick) {
    return <span title={title} style={chipStyle(color, false, colors)}>{inner}</span>;
  }
  return (
    <button type="button" onClick={onClick} title={title || 'Abrir a conversa neste canal'} style={chipStyle(color, true, colors)}>
      {inner}
    </button>
  );
};

/** Carrega os vinculos do contato; `links` pre-carregados pulam a chamada. */
export function useLinkedContacts(contactKey, reloadKey, preloaded) {
  const [links, setLinks] = useState(Array.isArray(preloaded) ? preloaded : []);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (Array.isArray(preloaded)) { setLinks(preloaded); return undefined; }
    if (!contactKey) { setLinks([]); return undefined; }
    let alive = true;
    setLoading(true);
    assignmentService.getLinkedContacts(contactKey)
      .then((res) => { if (alive) setLinks(Array.isArray(res?.data) ? res.data : []); })
      .catch(() => { if (alive) setLinks([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactKey, reloadKey, preloaded]);
  return { links, loading };
}

const ContactChannelChips = ({
  contactKey,
  channel,           // canal da conversa atual: wa_personal | wa_business | instagram | messenger | widget
  colors,
  variant = 'bar',
  reloadKey = 0,
  links: preloaded,  // opcional: vinculos ja carregados (o drawer os tem)
  onLink,            // drawer: abre o modal "Vincular a outro canal"
  onBeforeNavigate,  // ex.: fechar o drawer antes de trocar de inbox
  defaultChannel,    // canal padrao do usuario (escolhe /inbox ou /wa-business)
  style = {},
}) => {
  const navigate = useNavigate();
  const { flags } = useFeatureFlags();
  const { links } = useLinkedContacts(contactKey, reloadKey, preloaded);
  if (!flags?.lead_multichannel) return null;

  const abrir = (link) => {
    const alvo = linkedContactTarget(link, defaultChannel);
    if (!alvo) return;
    onBeforeNavigate?.();
    navigate(alvo.route, { state: alvo.state });
  };
  const outros = links.filter((l) => l && l.key);

  if (variant === 'cta') {
    const wa = outros.find((l) => linkedContactChannel(l) === 'whatsapp' && linkedContactTarget(l, defaultChannel));
    if (!wa) return null;
    return (
      <button
        type="button"
        onClick={() => abrir(wa)}
        title={linkedContactLabel(wa)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8,
          padding: '7px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
          backgroundColor: '#00a884', color: '#fff', fontSize: 12.5, fontWeight: 600,
          fontFamily: 'inherit', ...style,
        }}
      >
        <MessageCircle size={14} /> Continuar no WhatsApp
      </button>
    );
  }

  if (variant === 'bar') {
    if (outros.length === 0) return null;
    return (
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
          padding: '5px 16px', backgroundColor: colors.bgSecondary,
          borderBottom: `1px solid ${colors.borderColor}`, flexShrink: 0,
          fontSize: 11.5, color: colors.textSecondary, ...style,
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 600 }}>
          <Link2 size={12} /> Também no
        </span>
        {outros.map((l, i) => {
          const alvo = linkedContactTarget(l, defaultChannel);
          return (
            <Chip
              key={l.key || i}
              channel={linkedContactChannel(l)}
              label={linkedContactChipLabel(l)}
              title={alvo ? `Abrir ${linkedContactLabel(l)}` : linkedContactLabel(l)}
              onClick={alvo ? () => abrir(l) : undefined}
              colors={colors}
            />
          );
        })}
      </div>
    );
  }

  // variant === 'drawer'
  const atualCanal = channel || 'whatsapp';
  return (
    <div style={{
      margin: '10px 20px 0', padding: '10px 12px', borderRadius: 10,
      backgroundColor: colors.bgSecondary, display: 'flex', flexDirection: 'column', gap: 8, ...style,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: colors.textTertiary, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
          Canais desta pessoa
        </span>
        {onLink && (
          <button
            type="button"
            onClick={onLink}
            title="Vincular este contato a outro canal da mesma pessoa"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4, background: 'transparent',
              border: 'none', cursor: 'pointer', color: colors.accentPrimary,
              fontSize: 11.5, fontWeight: 600, padding: 0, fontFamily: 'inherit',
            }}
          >
            <Link2 size={12} /> Vincular canal
          </button>
        )}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
        <Chip channel={atualCanal} current colors={colors} title="Canal desta conversa" />
        {outros.map((l, i) => {
          const alvo = linkedContactTarget(l, defaultChannel);
          return (
            <Chip
              key={l.key || i}
              channel={linkedContactChannel(l)}
              label={linkedContactChipLabel(l)}
              title={alvo ? `Abrir ${linkedContactLabel(l)}` : `${linkedContactLabel(l)} (conversa não encontrada neste workspace)`}
              onClick={alvo ? () => abrir(l) : undefined}
              colors={colors}
            />
          );
        })}
      </div>
      <span style={{ fontSize: 11.5, color: colors.textSecondary, lineHeight: '16px' }}>
        {outros.length > 0
          ? 'Um lead só: etapa, responsável, tags e campos são os mesmos em todos os canais. Clique num canal para abrir a conversa dele.'
          : 'Esta pessoa só conversa por este canal. Se ela também escreve por outro, vincule para virar um lead só.'}
      </span>
    </div>
  );
};

export default ContactChannelChips;
